import React, { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import PageHeader from './PageHeader';
import type { BreadcrumbItem } from './PageHeader';
import SidebarBackdrop from './SidebarBackdrop';
import AppSidebar from '../AppSidebar/AppSidebar';
import AnalyticsSubSidebar from '../AppSidebar/AnalyticsSubSidebar';
import type { AppSidebarMenuItem } from '../AppSidebar/AppSidebar';
import { ResponsiveLayoutProvider } from '../../context/ResponsiveLayoutContext';
import { useMenuItems } from '../../hooks/useMenuItems';
import { mapMenuIconToSidebar } from '../../utils/sidebarIconMap';
import { isAnalyticsPath } from '../../utils/analyticsConfig';
import '../../theme/index.css';
import '../AppSidebar/AppSidebar.css';
import '../AppSidebar/AnalyticsSubSidebar.css';
import '../../theme/layout-shell.css';

interface PageLayoutProps {
  children: ReactNode;
  title: string;
  hideTitleInHeader?: boolean;
  breadcrumbs?: BreadcrumbItem[];
  primaryAction?: {
    label: string;
    onClick: () => void;
  };
  onBack?: () => void;
  /** Merged with `page-layout-content` for page-specific flex/scroll (e.g. Profile vs Templates). */
  contentClassName?: string;
  /** Light header bar (white background, dark text) for admin-style pages. */
  headerVariant?: 'default' | 'light';
}

const PageLayout: React.FC<PageLayoutProps> = ({
  children,
  title,
  hideTitleInHeader,
  breadcrumbs = [],
  primaryAction,
  onBack,
  contentClassName,
  headerVariant = 'default',
}) => {
  const location = useLocation();
  const customMenuItems = useMenuItems();
  const headerTitle = hideTitleInHeader ? '' : title;
  const showAnalyticsSubSidebar = isAnalyticsPath(location.pathname);

  const sidebarMenuItems: AppSidebarMenuItem[] = customMenuItems.map(item => ({
    title: item.title,
    path: item.path,
    icon: mapMenuIconToSidebar(item.icon),
  }));

  return (
    <ResponsiveLayoutProvider showAnalyticsSubSidebar={showAnalyticsSubSidebar}>
      <div className="page-layout">
        <AppSidebar menuItems={sidebarMenuItems} />
        {showAnalyticsSubSidebar ? <AnalyticsSubSidebar /> : null}
        <SidebarBackdrop />

        <div
          className={classNames(
            'page-layout-main',
            showAnalyticsSubSidebar && 'page-layout-main--with-analytics-sub'
          )}
        >
          <PageHeader
            title={headerTitle}
            breadcrumbs={breadcrumbs}
            primaryAction={primaryAction}
            onBack={onBack}
            variant={headerVariant}
          />

          <div
            className={
              contentClassName ? `page-layout-content ${contentClassName}` : 'page-layout-content'
            }
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              position: 'relative',
              backgroundColor: headerVariant === 'light' ? '#f8fafc' : 'var(--bg-page)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {children}
          </div>
        </div>
      </div>
    </ResponsiveLayoutProvider>
  );
};

export default PageLayout;
